/**
 * @file OverlayCard.tsx
 * @description Одна надпись на фото (phase19.16, партия SB120, этап 3а;
 * uiux-sb120-stage3 §3 «Надписи на фото»; данные db-design-sb120-stage3 §2
 * `overlay.preset / x / y / plate / typo`):
 *
 * ```
 * Текст  [ Сбор 2025 ]
 * Стиль  [НАДПИСЬ КАПСОМ][Акцентом]
 * Место  ┌─────────┐ x [ 0 ] %  y [ 100 ] %     ← по кадру фото, 9 подсказок,
 *        │●        │                              стрелки по 5 %; 12 px от края
 * Цвет · Шрифт · Размер ↺ 14                      ← ElementTypeFields (часть main)
 * Плашка [Нет][Тёмная ✓][Светлая][⌖ Свой] · Плотность · контраст по худшему
 * [ ] Скрыть на телефоне
 * ```
 *
 * Место — x/y % внутри кадра без 12 px от края; для {0, 50, 100} это прежние
 * 9 точек. Все ползунки — живой предпросмотр на канве (`…overlays.N.x` и т. п.).
 */

import { SECTION_TEXT_LIMITS, type SectionOverlay } from "@chaiberi/shared";

import { CheckRow } from "../../../site-view/CheckRow";
import { SectionTextField } from "../fields/SectionTextField";
import { DecorPositionField } from "../look/DecorPositionField";
import { useSectionForm } from "../section-form-context";
import { ElementTypeFields } from "./ElementTypeFields";
import { OverlayPlateFields } from "./OverlayPlateFields";
import { CaptionPresetField } from "./CaptionPresetField";
import { withTypo } from "../../../../lib/site-builder/element-typo";
import { captionBase } from "../../../../lib/site-builder/typo-base";

interface Props {
  overlay: SectionOverlay;
  onChange: (overlay: SectionOverlay) => void;
  /** Путь надписи: `columns.items.0.elements.1.overlays.0`. */
  path: string;
}

export function OverlayCard({ overlay, onChange, path }: Props) {
  const { preview, theme } = useSectionForm();
  const role = overlay.preset === "script" ? "accent" : "eyebrow";
  return (
    <>
      <SectionTextField label="Текст" path={`${path}.text`} value={overlay.text} max={SECTION_TEXT_LIMITS.overlay} onChange={(text) => onChange({ ...overlay, text })} />
      <CaptionPresetField value={overlay.preset} hint={null} onChange={(preset) => onChange({ ...overlay, preset })} />
      <DecorPositionField
        label="Место на фото"
        value={{ x: overlay.x, y: overlay.y }}
        onChange={({ x, y }) => onChange({ ...overlay, x, y })}
        onPreview={(axis, v) => preview(`${path}.${axis}`, v)}
        schemeClassName="aspect-[4/3]"
        hint="Схема — кадр фото; надпись держится в 12 px от края."
      />
      <ElementTypeFields
        parts={[{ part: "", role, key: "main" }]}
        typo={overlay.typo}
        onChange={(typo) => onChange(withTypo(overlay, typo))}
        path={path}
        baseOf={() => captionBase(overlay.preset, theme)}
        colorAutoLabel="Авто: светлый"
      />
      <OverlayPlateFields overlay={overlay} onChange={onChange} path={path} />
      <CheckRow checked={overlay.hideOnPhone} onChange={(hideOnPhone) => onChange({ ...overlay, hideOnPhone })}>
        Скрыть на телефоне
      </CheckRow>
    </>
  );
}
